/**
 * Response shapes for endpoints whose types live in apps/worker (not
 * packages/shared, which only holds the core domain model this app
 * imports directly — ReceivingItem, ReceivingSession, CorrectionReason,
 * etc). Deliberately not depending on @warehouse/worker itself (it pulls
 * in @cloudflare/workers-types and D1/R2/Queue bindings that make no
 * sense in a browser bundle) — these are hand-mirrored from the actual
 * route handlers in apps/worker/src/index.ts and the service functions
 * they call. Keep in sync by hand if those shapes change; each field
 * below is commented with its source.
 */
import type { ReceivingItem, ReceivingSession, ScanEvent, ReceivingException, CorrectionReason, StockCount } from '@warehouse/shared';

/** apps/worker/src/services/dashboard.ts's AsnDashboardSummary. */
export interface AsnDashboardSummary {
  asnId: string;
  poNumber: string;
  mintsoftAsnId: number | null;
  sessionId: string | null;
  sessionStatus: ReceivingSession['status'] | null;
  expectedQuantity: number;
  receivedQuantity: number;
  variance: number;
  discrepantLineCount: number;
  updatedAt: string;
}

/** apps/worker/src/services/userRepository.ts's UserRecord, returned by GET /api/me. */
export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: 'OPERATOR' | 'SUPERVISOR' | 'ADMIN';
  active: boolean;
  /** This login may hand batches to pickers and mark batches picked (ticked on the admin Operators page). */
  canRelease?: boolean;
  /** Open short orders (only for logins that can release batches). */
  shortOpen?: number;
}

/** One locked batch on the phone Batches screen (GET /api/release/batches). */
export interface ReleaseBatch {
  runId: string;
  runDate: string;
  runLabel: string;
  batchId: string;
  name: string;
  group: string;
  planPicker: string;
  assignedUserId: string;
  assignedUserName: string;
  status: string;
  total: number;
  picked: number;
  short: number;
}
/** A short order (a picker gave up on an item) waiting for the office. */
export interface ShortOrder {
  batchId: string;
  batchName: string;
  orderId: number;
  orderNumber: string;
  sku: string;
  location: string;
  qty: number;
  note: string;
  operatorName: string;
  at: string;
}
export interface ReleaseList {
  batches: ReleaseBatch[];
  users: Array<{ id: string; name: string }>;
  shorts?: ShortOrder[];
}

/** apps/worker/src/index.ts's GET /api/auth/operators — the name picker shown on the Login screen. Active operators only; no PIN/email/role leaked pre-login. */
export interface OperatorSummary {
  id: string;
  name: string;
}

/** apps/worker/src/services/auth.ts's LoginResult, returned by POST /api/auth/login. */
export interface LoginResult {
  token: string;
  user: CurrentUser;
  expiresAt: string;
}

/** apps/worker/src/index.ts's GET /api/receiving/:sessionId. */
export interface SessionWithItems {
  session: ReceivingSession;
  items: ReceivingItem[];
}

/** apps/worker/src/services/receivingSession.ts's ScanOutcome. */
export type ScanOutcome =
  | { kind: 'SCANNED'; item: ReceivingItem; event: ScanEvent }
  | { kind: 'UNKNOWN_BARCODE'; ean: string }
  | { kind: 'ALREADY_APPLIED' };

/** apps/worker/src/services/receivingSession.ts's UndoOutcome. */
export type UndoOutcome = { kind: 'UNDONE'; item: ReceivingItem; event: ScanEvent } | { kind: 'NOTHING_TO_UNDO' };

/** apps/worker/src/services/receivingSession.ts's ManualEditOutcome. */
export type ManualEditOutcome =
  | { kind: 'APPLIED'; item: ReceivingItem; event: ScanEvent }
  | { kind: 'REQUIRES_LARGE_CHANGE_CONFIRMATION'; delta: number }
  | { kind: 'REJECTED'; reason: string };

/** apps/worker/src/services/receivingSession.ts's ReviewLine. */
export interface ReviewLine {
  receivingItemId: string;
  sku: string;
  ean: string;
  expectedQuantity: number;
  receivedQuantity: number;
  variance: number;
  edited: boolean;
  scanCount: number;
}

/** apps/worker/src/services/receivingSession.ts's ConfirmOutcome. */
export type ConfirmOutcome =
  | { kind: 'CONFIRMED'; session: ReceivingSession; confirmationId: string }
  | { kind: 'ALREADY_CONFIRMED'; session: ReceivingSession };

/**
 * apps/worker/src/services/receivingPartialPush.ts's PushPartialReceivingOutcome,
 * returned by POST /api/receiving/:sessionId/confirm-partial — the
 * instalment-delivery "Confirm this delivery" action (2026-10-01). Unlike
 * ConfirmOutcome above, this never locks the session: scanning can
 * continue afterwards for later instalments.
 */
export interface PartialPushLineResult {
  receivingItemId: string;
  sku: string;
  deltaPushed: number;
}
export type PartialPushOutcome =
  | { kind: 'PUSHED'; lines: PartialPushLineResult[]; partBookSucceeded: boolean }
  | { kind: 'NOTHING_TO_PUSH' }
  | { kind: 'SESSION_LOCKED' };

/**
 * apps/worker/src/services/stockCount.ts's RecordStockCountOutcome,
 * returned by POST /api/stock-counts (the mobile scan screen's one call
 * per count — Suri, 2026-10-05's Stock Count feature).
 */
export type RecordStockCountOutcome =
  | { kind: 'RECORDED'; stockCount: StockCount; supersededPreviousId: string | null }
  | { kind: 'PRODUCT_NOT_FOUND' }
  | { kind: 'LOCATION_NOT_FOUND'; location: string };

/** GET /api/stock-counts/expected — what Mintsoft says is in a location for a SKU, shown before the operator types the count. */
export interface ExpectedQuantity {
  kind: 'OK';
  sku: string;
  productName: string | null;
  locationName: string;
  /** null = the location report could not be read; the screen then counts "blind". */
  expectedQuantity: number | null;
  pendingCountedQuantity: number | null;
}

/** GET /api/stock-counts/expected-sku — what Mintsoft says is in the WHOLE warehouse for a SKU (SKU count, 2026-10-16). */
export interface ExpectedSkuTotal {
  kind: 'OK';
  sku: string;
  productName: string | null;
  /** null = the location report could not be read; the screen then counts "blind". */
  expectedQuantity: number | null;
  locations: Array<{ location: string; quantity: number }>;
  pendingCountedQuantity: number | null;
}

/** GET /api/stock-lookup — read-only stock check for one scanned SKU (apps/worker/src/services/stockLookup.ts). */
export interface StockLookupLocation {
  name: string;
  quantity: number;
  allocated: number;
  isPickLocation: boolean;
}
export interface StockLookupWarehouse {
  warehouseId: number;
  stockLevel: number;
  onHand: number;
  allocated: number;
  inQuarantine: number;
  locations: StockLookupLocation[] | null;
  locationsTotal: number | null;
  pickLocationName: string | null;
}
export interface StockLookupResult {
  kind: 'OK';
  productId: number;
  sku: string;
  productName: string | null;
  warehouses: StockLookupWarehouse[];
  readAt: string;
}

/** apps/worker/src/services/stockCount.ts's ApplyStockCountResult, one per id in POST /api/stock-counts/apply's response array. */
export interface ApplyStockCountResult {
  id: string;
  sku: string;
  outcome: 'APPLIED' | 'FAILED' | 'SKIPPED_NOT_PENDING';
  onHandBefore?: number;
  onHandAfter?: number;
  driftSinceCount?: number;
  message?: string;
}

export type { ReceivingItem, ReceivingSession, ReceivingException, CorrectionReason, StockCount };

export const CORRECTION_REASONS: CorrectionReason[] = [
  'ACCIDENTAL_SCAN',
  'DAMAGED_GOODS',
  'DUPLICATE_SCAN',
  'WRONG_QUANTITY',
  'SCANNER_ERROR',
  'PHYSICAL_COUNT_CORRECTION',
  'OTHER',
];

/** Phone picking (Suri, 2026-10-09). One completed order = one pick for pay. */
export interface PickBatchSummary {
  runId: string;
  batchId: string;
  name: string;
  group: string;
  picker: string;
  runDate: string;
  label: string;
  orders: number;
  units: number;
  picked: number;
  /** Orders given up on ("short"). */
  short: number;
  mine: boolean;
  /** Name of the login the office gave the batch to ('' = nobody yet). */
  assignedTo: string;
  labelCheck: boolean;
}
export interface PickLineView {
  idx: number;
  sku: string;
  qty: number;
  location: string;
  done: boolean;
  manual: boolean;
}
export interface PickOrderView {
  orderId: number;
  orderNumber: string;
  firstName: string;
  courier: string;
  lines: PickLineView[];
  complete: boolean;
  skips: number;
  short: boolean;
  labelNeeded: boolean;
  labelDone: boolean;
}
/** One thing to do: take an item, or scan the order's label. queue[0] is "now". */
export interface PickStep {
  kind: 'LINE' | 'LABEL';
  orderId: number;
  orderNumber: string;
  firstName: string;
  lineIdx: number;
  sku: string;
  qty: number;
  location: string;
  listOnHand?: number;
  itemNo: number;
  items: number;
  retry: boolean;
}
/** Stock shown beside "Take N" on the picking screen. */
export interface PickStock {
  sku: string;
  onHand: number;
  available: number;
  allocated: number;
  /** null = the per-location report could not be read. */
  here: { onHand: number; free: number } | null;
  readAt: string;
}
export interface BatchPickView {
  runId: string;
  batchId: string;
  name: string;
  group: string;
  picker: string;
  assignedUserId: string;
  assignedUserName: string;
  labelCheck: boolean;
  total: number;
  picked: number;
  short: number;
  units: number;
  excluded: number;
  orders: PickOrderView[];
  queue: PickStep[];
}
export interface PickConfirmResult {
  order: PickOrderView;
  picked: number;
  total: number;
  short: number;
  alreadyDone: boolean;
  orderCompleted: boolean;
  queue: PickStep[];
  unverified?: boolean;
}
export type PickSkipReason = 'NOT_AT_LOCATION' | 'DAMAGED' | 'WRONG_LOCATION' | 'OTHER';

/** Phone Replen (apps/worker/src/services/replen): runs = what to carry from one bulk shelf. */
export interface ReplenRunLine {
  sku: string;
  productId: number;
  productName: string;
  pickLocation: string;
  quantity: number;
  available: number;
  current: number;
  replenPoint: number;
  size: number;
  needed: number;
}
export interface ReplenRun {
  source: string;
  sourceType: string;
  lines: ReplenRunLine[];
  units: number;
}
export interface ReplenList {
  runs: ReplenRun[];
  nothingToBring: number;
  unchecked: number;
  pendingLookups: number;
  readAt: string;
}
export interface ReplenMoveResult {
  kind: 'DONE' | 'FAILED' | 'UNSURE' | 'IN_PROGRESS';
  message: string;
}
